import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  Popover,
  TablePagination,
  Alert,
  styled
} from '@mui/material';
import { ManageSearch as ManageSearchIcon, Search as SearchIcon } from '@mui/icons-material';
import Lottie from 'lottie-react';
import listaAnimation from './lista-animation.json';
import { ContextSession } from "../../services/ContextSession";

// Componentes estilizados
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: '#19438C',
    color: 'white',
    fontSize: 16,
    fontWeight: 'bolder',
  },
  '&.MuiTableCell-body': {
    fontSize: 14,
    borderRight: 'none',
    borderLeft: 'none',
  },
}));

const ListarPartes = () => {
  let sessi = useContext(ContextSession);

  // Estados optimizados
  const [state, setState] = useState({
    partes: [],
    loading: true,
    error: null,
    page: 0,
    rowsPerPage: 10,
    searchTerm: '',
    filterAnchor: null,
    filterType: ''
  });

  // Destructuración para mejor legibilidad
  const { partes, loading, error, page, rowsPerPage, searchTerm, filterAnchor, filterType } = state;

  // Efecto para cargar datos
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessi.sessionData.token; // Asegúrate de que sessi esté accesible

        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/partes/listar/', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) throw new Error(`Error HTTP: ${response.status}`);
        const data = await response.json();
        setState(prev => ({ ...prev, partes: data, loading: false }));
      } catch (err) {
        setState(prev => ({ ...prev, error: err.message, loading: false }));
      }
    };

    fetchData();
  }, []);


  // Handlers consolidados
  const handleStateChange = (updates) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const handleSearch = (e) => {
    handleStateChange({ searchTerm: e.target.value, page: 0 });
  };

  const handleSearchIconClick = (e, type) => {
    handleStateChange({
      filterAnchor: e.currentTarget,
      filterType: type,
      searchTerm: ''
    });
  };

  // Funciones utilitarias
  const formatFecha = (fechaString) => {
    if (!fechaString) return 'N/A';
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  // Filtrado mejorado
  const filteredPartes = partes.filter(parte =>
    Object.entries({
      'id': parte.ParteID?.toString(),
      'identificador': parte.identificador,
      'nombre': parte.NombreParte,
      'telefono': parte.Telefono,
      'correo': parte.Correo,
      'direccion': parte.Direccion
    }).some(([key, value]) =>
      filterType ?
        (key === filterType && value?.toLowerCase().includes(searchTerm.toLowerCase())) :
        value?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const paginatedPartes = filteredPartes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Renderizado condicional
  if (loading) {
    return (
      <Container sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh'
      }}>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Cargando datos de partes...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ width: '100%' }}>
          Error al cargar datos: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 1, mb: 1 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{
        fontWeight: 'bold',
        mb: 1
      }}>
        Listado de Partes
      </Typography>

      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '200%'
      }}>
        <Box sx={{ width: 150, height: 100 }}>
          <Lottie
            animationData={listaAnimation}
            loop={true}
            autoplay={true}
          />
        </Box>
      </Box>

      {/* Tabla de partes */}
      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 750 }} bold="Tabla de partes">
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  ID Parte
                  <Tooltip title="Buscar por ID">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'id')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  Identificador
                  <Tooltip title="Buscar por identificador">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'identificador')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  Nombre
                  <Tooltip title="Buscar por nombre">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'nombre')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  Teléfono
                  <Tooltip title="Buscar por teléfono">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'telefono')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  Correo
                  <Tooltip title="Buscar por correo">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'correo')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  Dirección
                  <Tooltip title="Buscar por dirección">
                    <IconButton
                      size="small"
                      onClick={(e) => handleSearchIconClick(e, 'direccion')}
                      sx={{ color: 'common.white', ml: 1 }}
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">Fecha Creación</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedPartes.length > 0 ? (
              paginatedPartes.map((parte) => (
                <StyledTableRow key={parte.ParteID}>
                  <TableCell align="center">{parte.ParteID}</TableCell>
                  <TableCell align="center">{parte.identificador || 'N/A'}</TableCell>
                  <TableCell align="center">{parte.NombreParte || 'N/A'}</TableCell>
                  <TableCell align="center">{parte.Telefono || 'N/A'}</TableCell>
                  <TableCell align="center">
                    {parte.Correo ? (
                      <a href={`mailto:${parte.Correo}`} style={{ color: '#1976d2' }}>
                        {parte.Correo.toLowerCase()}
                      </a>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell align="center">{parte.Direccion || 'N/A'}</TableCell>
                  <TableCell align="center">{formatFecha(parte.FechaCreacion)}</TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No se encontraron resultados
                  </Typography>
                  {searchTerm && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Intente con otro término de búsqueda
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Popover de búsqueda */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => handleStateChange({ filterAnchor: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Buscar por ${filterType === 'id' ? 'ID' :
              filterType === 'identificador' ? 'identificador' :
                filterType === 'nombre' ? 'nombre' :
                  filterType === 'telefono' ? 'teléfono' :
                    filterType === 'correo' ? 'correo' :
                      filterType === 'direccion' ? 'dirección' :
                        'cualquier campo'
              }`}
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Box>
      </Popover>

      {/* Paginación */}
      {filteredPartes.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredPartes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => handleStateChange({ page: newPage })}
          onRowsPerPageChange={(e) => handleStateChange({
            rowsPerPage: parseInt(e.target.value, 10),
            page: 0
          })}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ mt: 2 }}
        />
      )}
    </Container>
  );
};

export default ListarPartes;